import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Calculator, ArrowLeft, AlertTriangle, FileText, Pencil, Trash2, RefreshCw } from "lucide-react";

export default function OrderDetail() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [calcError, setCalcError] = useState("");
    const [calcing, setCalcing] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [loadError, setLoadError] = useState("");
    const nav = useNavigate();

    const load = useCallback(async () => {
        setLoadError("");
        try {
            const { data } = await api.get(`/orders/${id}`);
            setData(data);
        } catch (e) {
            setLoadError(formatApiErrorDetail(e.response?.data?.detail ?? "Bestelling kon niet worden geladen"));
        }
    }, [id]);
    useEffect(() => {
        load();
    }, [load]);

    const calc = async () => {
        setCalcError("");
        setCalcing(true);
        try {
            const r = await api.post(`/orders/${id}/calculate`);
            nav(`/berekening/${r.data.id}`);
        } catch (e) {
            setCalcError(formatApiErrorDetail(e.response?.data?.detail ?? "Berekening mislukt"));
        } finally {
            setCalcing(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await api.delete(`/orders/${id}`);
            toast.success("Bestelling verwijderd");
            nav("/bestellingen");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail ?? "Verwijderen mislukt"));
            setDeleting(false);
        }
    };

    if (loadError) {
        return <div className="p-12 text-red-800" role="alert">{loadError} <button type="button" onClick={load} className="font-semibold underline">Opnieuw proberen</button></div>;
    }
    if (!data) return <div className="p-12 text-ink-500">Laden…</div>;
    const orphanLines = data.lines.filter((l) => !l.product);
    const hasOrphans = orphanLines.length > 0;
    const updatedAt = data.updated_at && new Date(data.updated_at);
    const createdAt = data.created_at && new Date(data.created_at);
    const wasEdited = data.updated_at && data.created_at && data.updated_at !== data.created_at;
    const lastCalcAt = data.last_calculation_at && new Date(data.last_calculation_at);
    const isStale = !!data.is_stale;

    return (
        <div className="p-5 sm:p-7 lg:p-10 xl:p-12 max-w-[1180px] mx-auto pb-24 lg:pb-12">
            <Link
                to="/bestellingen"
                className="text-sm text-ink-500 hover:text-florefy-900 inline-flex items-center gap-1 mb-4"
            >
                <ArrowLeft className="h-3.5 w-3.5" /> Terug naar bestellingen
            </Link>
            <PageHeader
                kicker={`Order · ${data.id.slice(0, 8)}`}
                title={data.customer?.name || "Onbekende klant"}
                subtitle={`Leverdatum ${data.delivery_date} · ${data.lines.length} regels`}
                testid="order-detail-header"
                actions={
                    <>
                        <Link
                            to={`/bestellingen/${id}/bewerken`}
                            data-testid="btn-edit-order"
                            className="px-4 py-2 rounded-sm border border-border bg-white hover:bg-ink-50 text-sm flex items-center gap-2"
                        >
                            <Pencil className="h-4 w-4" /> Bewerken
                        </Link>
                        <button
                            onClick={() => setConfirmDelete(true)}
                            disabled={deleting}
                            data-testid="btn-delete-order"
                            className="px-4 py-2 rounded-sm border border-destructive/40 bg-white hover:bg-destructive/5 text-destructive text-sm flex items-center gap-2 disabled:opacity-50"
                        >
                            <Trash2 className="h-4 w-4" /> Verwijderen
                        </button>
                        <Link
                            to={`/bestellingen/${id}/samenvatting`}
                            data-testid="btn-summary"
                            className="px-4 py-2 rounded-sm border border-border bg-white hover:bg-ink-50 text-sm flex items-center gap-2"
                        >
                            <FileText className="h-4 w-4" /> Samenvatting
                        </Link>
                        {data.last_calculation_id && (
                            <Link
                                to={`/berekening/${data.last_calculation_id}`}
                                className="px-4 py-2 rounded-sm border border-border bg-white hover:bg-ink-50 text-sm"
                                data-testid="btn-view-calc"
                            >
                                Bekijk berekening
                            </Link>
                        )}
                        <button
                            onClick={calc}
                            disabled={calcing || hasOrphans}
                            title={hasOrphans ? "Eerst de ontbrekende producten oplossen" : ""}
                            data-testid="btn-recalculate"
                            className="px-4 py-2 rounded-sm bg-florefy-900 hover:bg-florefy-700 text-white text-sm flex items-center gap-2 disabled:opacity-50"
                        >
                            <Calculator className="h-4 w-4" />
                            {calcing ? "Bezig…" : (data.last_calculation_id ? "Opnieuw berekenen" : "Bereken karren")}
                        </button>
                    </>
                }
            />

            {confirmDelete && (
                <div
                    className="bg-destructive/10 border border-destructive text-destructive text-sm p-4 mb-4 rounded-sm"
                    data-testid="confirm-delete-banner"
                >
                    <div className="flex items-start gap-2 mb-3">
                        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                        <div>
                            Weet je zeker dat je deze bestelling én alle bijbehorende berekeningen
                            wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
                        </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                        <button
                            onClick={() => setConfirmDelete(false)}
                            className="px-3 py-1.5 text-xs border border-border bg-white rounded-sm hover:bg-ink-50"
                            data-testid="btn-cancel-delete"
                        >
                            Annuleren
                        </button>
                        <button
                            onClick={handleDelete}
                            disabled={deleting}
                            data-testid="btn-confirm-delete"
                            className="px-3 py-1.5 text-xs bg-destructive text-white rounded-sm hover:opacity-90 disabled:opacity-50"
                        >
                            {deleting ? "Verwijderen…" : "Ja, definitief verwijderen"}
                        </button>
                    </div>
                </div>
            )}

            {isStale && (
                <div
                    className="bg-yellow-50 border border-yellow-400 text-yellow-900 text-sm p-4 mb-6 rounded-sm flex gap-3"
                    data-testid="stale-warning"
                >
                    <RefreshCw className="h-5 w-5 shrink-0 mt-0.5 text-yellow-700" />
                    <div>
                        <div className="font-semibold">
                            Deze bestelling is bewerkt na de laatste berekening
                        </div>
                        <div className="mt-1 text-xs">
                            Laatst gewijzigd: <span className="font-mono">{updatedAt.toLocaleString("nl-NL")}</span>{" "}
                            · Laatste berekening:{" "}
                            <span className="font-mono">{lastCalcAt ? lastCalcAt.toLocaleString("nl-NL") : "—"}</span>.
                            Druk op "Opnieuw berekenen" en print de samenvatting / labels opnieuw zodat de schuur de juiste versie heeft.
                        </div>
                    </div>
                </div>
            )}

            {calcError && (
                <div
                    className="bg-destructive/10 border border-destructive text-destructive text-sm p-3 mb-4 rounded-sm flex gap-2"
                    data-testid="calc-error"
                >
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    {calcError}
                </div>
            )}

            {hasOrphans && (
                <div
                    className="bg-yellow-50 border border-yellow-400 text-yellow-900 text-sm p-4 mb-6 rounded-sm flex gap-3"
                    data-testid="orphan-warning"
                >
                    <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5 text-yellow-700" />
                    <div>
                        <div className="font-semibold">
                            {orphanLines.length} orderregel(s) verwijzen naar verwijderde producten
                        </div>
                        <div className="mt-1 text-xs">
                            Deze regels staan rood gemarkeerd in de tabel. De berekening kan pas
                            draaien als alle regels een geldig product hebben. Maak een nieuwe
                            bestelling aan met de juiste producten, of voeg de verwijderde
                            producten opnieuw toe op de Producten-pagina.
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-white border border-border rounded-sm p-5 lg:col-span-1 space-y-3">
                    <Row label="Klant" value={data.customer?.name} />
                    <Row label="Adres" value={data.customer?.address} />
                    <Row label="Route" value={data.customer?.route_code} />
                    <Row label="Leverdatum" value={data.delivery_date} mono />
                    <Row label="Status" value={data.status} mono />
                    <Row
                        label="Aangemaakt"
                        value={createdAt ? createdAt.toLocaleString("nl-NL") : "—"}
                        mono
                    />
                    {wasEdited && (
                        <Row
                            label="Laatst gewijzigd"
                            value={updatedAt ? updatedAt.toLocaleString("nl-NL") : "—"}
                            mono
                            testid="row-updated-at"
                        />
                    )}
                    {data.notes && <Row label="Notities" value={data.notes} />}
                </div>

                <div className="bg-white border border-border rounded-sm lg:col-span-2">
                    <div className="px-5 py-4 border-b border-border">
                        <h2 className="text-base font-semibold text-ink-900">Orderregels</h2>
                    </div>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-ink-50">
                                <th className="text-left px-4 py-3 data-label">SKU</th>
                                <th className="text-left px-4 py-3 data-label">Product</th>
                                <th className="text-left px-4 py-3 data-label">Locatie</th>
                                <th className="text-right px-4 py-3 data-label">Aantal</th>
                                <th className="text-right px-4 py-3 data-label">Stuks/Laag</th>
                                <th className="text-right px-4 py-3 data-label">Lagen</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.lines.map((l) => {
                                const layers = l.product
                                    ? Math.ceil(l.quantity / l.product.units_per_layer)
                                    : 0;
                                const orphan = !l.product;
                                return (
                                    <tr
                                        key={l.id}
                                        className={`border-t border-border/60 ${
                                            orphan ? "bg-destructive/5" : ""
                                        }`}
                                        data-testid={`order-line-${l.id}`}
                                    >
                                        <td className="px-4 py-3 font-mono">
                                            {l.product?.sku || (
                                                <span className="text-destructive font-semibold">
                                                    ⚠ ONTBREEKT
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            {l.product ? (
                                                <>
                                                    {l.product.name}
                                                    <span className="text-ink-500 italic ml-1">
                                                        {l.product.cultivar}
                                                    </span>
                                                </>
                                            ) : (
                                                <span className="text-destructive text-xs font-mono">
                                                    product-id: {l.product_id?.slice(0, 8)}…
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            {l.location ? (
                                                <span className="inline-flex items-center gap-1 text-xs font-mono px-2 py-0.5 rounded-sm border border-ink-200" style={{ backgroundColor: (l.location.color || "#e5e7eb") + "33" }} title={l.location.name}>
                                                    {l.location.code} · {l.location.name}
                                                </span>
                                            ) : (
                                                <span className="text-yellow-700 text-xs font-mono">geen</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 font-mono text-right">
                                            {l.quantity}
                                        </td>
                                        <td className="px-4 py-3 font-mono text-right text-ink-500">
                                            {l.product?.units_per_layer || "—"}
                                        </td>
                                        <td className="px-4 py-3 font-mono text-right font-medium">
                                            {layers || "—"}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function Row({ label, value, mono, testid }) {
    return (
        <div data-testid={testid}>
            <div className="data-label">{label}</div>
            <div className={mono ? "font-mono text-ink-900" : "text-ink-900"}>
                {value || "—"}
            </div>
        </div>
    );
}
